import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  createGoal, 
  getGoals, 
  getGoalById, 
  updateGoal, 
  deleteGoal, 
  updateGoalStatus,
  getGoalsStats,
  migrateLocalStorageGoals,
  hasLocalStorageData,
  getLocalStorageData,
  clearLocalStorageData
} from '@/repos/goalsRepo';
import type { 
  Goal, 
  GoalFormData, 
  GoalFilters, 
  GoalSortOptions, 
  GoalsStats 
} from '@/types/goals';
import { convertLocalStorageToSupabase, convertSupabaseToLocalStorage } from '@/types/goals';

interface UseGoalsReturn {
  // Data
  goals: Goal[];
  stats: GoalsStats | null;
  
  // Loading states
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  migrating: boolean;
  
  // Error states
  error: string | null;
  
  // Actions
  createGoal: (goalData: GoalFormData) => Promise<boolean>;
  updateGoal: (goalId: string, updates: Partial<GoalFormData>) => Promise<boolean>;
  deleteGoal: (goalId: string) => Promise<boolean>;
  updateGoalStatus: (goalId: string, newStatus: string) => Promise<boolean>;
  refreshGoals: () => Promise<void>;
  migrateLocalStorage: () => Promise<boolean>;
  
  // Filters and sorting
  setFilters: (filters: GoalFilters) => void;
  setSortOptions: (sortOptions: GoalSortOptions) => void;
  
  // Migration status
  hasLocalData: boolean;
  migrationCompleted: boolean;
}

export function useGoals(): UseGoalsReturn {
  const { user } = useAuth();
  
  // State
  const [goals, setGoals] = useState<Goal[]>([]);
  const [stats, setStats] = useState<GoalsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<GoalFilters>({});
  const [sortOptions, setSortOptions] = useState<GoalSortOptions>({
    field: 'created_at',
    direction: 'DESC'
  });
  const [hasLocalData, setHasLocalData] = useState(false);
  const [migrationCompleted, setMigrationCompleted] = useState(false);

  // Check for localStorage data on mount
  useEffect(() => {
    setHasLocalData(hasLocalStorageData());
  }, []);

  // Load goals when user changes or filters/sort change
  const loadGoals = useCallback(async () => {
    if (!user?.id) {
      setGoals([]);
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Carregando metas do banco...');

      // Load goals
      const goalsResponse = await getGoals(user.id, filters, sortOptions);
      if (goalsResponse.error) {
        setError(goalsResponse.error);
        return;
      }

      console.log('📊 Metas carregadas do banco:', goalsResponse.data.length, 'metas');

      // Convert Supabase data to localStorage format for compatibility
      const compatibleGoals = goalsResponse.data.map(convertSupabaseToLocalStorage);
      
      console.log('🔄 Metas convertidas para formato local:', compatibleGoals.length, 'metas');
      
      // Log das datas para debug
      compatibleGoals.forEach(goal => {
        if (goal.dataInicio || goal.dataAlvo) {
          console.log('📅 Meta com datas:', {
            id: goal.id,
            titulo: goal.titulo,
            dataInicio: goal.dataInicio,
            dataAlvo: goal.dataAlvo
          });
        }
      });
      
      setGoals(compatibleGoals as any);

      // Load stats
      const statsResponse = await getGoalsStats(user.id);
      if (statsResponse.error) {
        console.warn('Error loading stats:', statsResponse.error);
      } else {
        setStats(statsResponse.data);
      }
    } catch (err) {
      console.error('Error loading goals:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters, sortOptions]);

  // Load goals on mount and when dependencies change
  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  // Create goal
  const handleCreateGoal = useCallback(async (goalData: GoalFormData): Promise<boolean> => {
    if (!user?.id) {
      setError('User not authenticated');
      return false;
    }

    try {
      setCreating(true);
      setError(null);

      const response = await createGoal(goalData, user.id);
      if (response.error) {
        setError(response.error);
        return false;
      }

      // Refresh goals list
      await loadGoals();
      return true;
    } catch (err) {
      console.error('Error creating goal:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setCreating(false);
    }
  }, [user?.id, loadGoals]);

  // Update goal
  const handleUpdateGoal = useCallback(async (goalId: string, updates: Partial<GoalFormData>): Promise<boolean> => {
    if (!user?.id) {
      setError('User not authenticated');
      return false;
    }

    try {
      setUpdating(true);
      setError(null);

      console.log('🔄 Atualizando meta:', { goalId, updates });

      const response = await updateGoal(goalId, updates, user.id);
      if (response.error) {
        setError(response.error);
        return false;
      }

      console.log('✅ Meta atualizada com sucesso, recarregando dados...');

      // Refresh goals list with a small delay to ensure database is updated
      setTimeout(async () => {
        console.log('🔄 Recarregando lista de metas...');
        await loadGoals();
      }, 100);
      
      return true;
    } catch (err) {
      console.error('Error updating goal:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setUpdating(false);
    }
  }, [user?.id, loadGoals]);

  // Delete goal
  const handleDeleteGoal = useCallback(async (goalId: string): Promise<boolean> => {
    if (!user?.id) {
      setError('User not authenticated');
      return false;
    }

    try {
      setDeleting(true);
      setError(null);

      const response = await deleteGoal(goalId, user.id);
      if (!response.success) {
        setError(response.error || 'Failed to delete goal');
        return false;
      }

      // Refresh goals list
      await loadGoals();
      return true;
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setDeleting(false);
    }
  }, [user?.id, loadGoals]);

  // Update goal status
  const handleUpdateGoalStatus = useCallback(async (goalId: string, newStatus: string): Promise<boolean> => {
    if (!user?.id) {
      setError('User not authenticated');
      return false;
    }

    try {
      setUpdating(true);
      setError(null);

      const response = await updateGoalStatus(goalId, newStatus, user.id);
      if (response.error) {
        setError(response.error);
        return false;
      }

      // Refresh goals list
      await loadGoals();
      return true;
    } catch (err) {
      console.error('Error updating goal status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setUpdating(false);
    }
  }, [user?.id, loadGoals]);

  // Refresh goals
  const refreshGoals = useCallback(async () => {
    await loadGoals();
  }, [loadGoals]);

  // Migrate localStorage data
  const handleMigrateLocalStorage = useCallback(async (): Promise<boolean> => {
    if (!user?.id || !hasLocalData) {
      return false;
    }

    try {
      setMigrating(true);
      setError(null);

      const localData = getLocalStorageData();
      if (localData.length === 0) {
        setMigrationCompleted(true);
        return true;
      }

      const response = await migrateLocalStorageGoals(user.id, localData);
      if (!response.success) {
        setError(response.error || 'Failed to migrate data');
        return false;
      }

      // Clear localStorage after successful migration
      clearLocalStorageData();
      setHasLocalData(false);
      setMigrationCompleted(true);

      // Refresh goals list
      await loadGoals();
      return true;
    } catch (err) {
      console.error('Error migrating localStorage data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setMigrating(false);
    }
  }, [user?.id, hasLocalData, loadGoals]);

  // Update filters
  const handleSetFilters = useCallback((newFilters: GoalFilters) => {
    setFilters(newFilters);
  }, []);

  // Update sort options
  const handleSetSortOptions = useCallback((newSortOptions: GoalSortOptions) => {
    setSortOptions(newSortOptions);
  }, []);

  return {
    // Data
    goals,
    stats,
    
    // Loading states
    loading,
    creating,
    updating,
    deleting,
    migrating,
    
    // Error states
    error,
    
    // Actions
    createGoal: handleCreateGoal,
    updateGoal: handleUpdateGoal,
    deleteGoal: handleDeleteGoal,
    updateGoalStatus: handleUpdateGoalStatus,
    refreshGoals,
    migrateLocalStorage: handleMigrateLocalStorage,
    
    // Filters and sorting
    setFilters: handleSetFilters,
    setSortOptions: handleSetSortOptions,
    
    // Migration status
    hasLocalData,
    migrationCompleted
  };
}
